import { Box, Paper, Typography } from "@mui/material";
import AppButton from "../../Common/Button/Button";
import InfoField from "../../Common/InfoField/InfoField";
import {
  getNotificationTemplateTypeLabel,
  getPushCategoryOptionsForType,
  notificationSeverityOptions,
  notificationTemplateTypes,
  pushNotificationTypeOptions,
} from "../notificationConstant";
import { getOptionLabel, normalizeTemplateContent } from "../notificationFormUtils";
import { emailSectionCardSx } from "../emailFormStyles";
import { format } from "date-fns";

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return format(date, "dd MMM yyyy 'at' hh:mm a");
};

const formattedHtmlSx = {
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: 1.5,
  whiteSpace: "pre-wrap",
  wordWrap: "break-word",
  "& b, & strong": { fontWeight: 700 },
  "& i, & em": { fontStyle: "italic" },
  "& u, & ins": { textDecoration: "underline" },
  "& s, & strike, & del": { textDecoration: "line-through" },
  "& ul, & ol": {
    display: "block",
    margin: "0 0 0.75em",
    paddingLeft: "1.5em",
    listStylePosition: "outside",
  },
  "& ul": { listStyle: "disc outside" },
  "& ol": { listStyle: "decimal outside" },
  "& li": {
    display: "list-item",
    listStyle: "inherit",
  },
  "& p": { margin: "0 0 0.75em" },
  "& p:last-child": { marginBottom: 0 },
  "& img": { maxWidth: "100%" },
};

const previewImageSx = {
  width: "100%",
  maxWidth: 220,
  maxHeight: 140,
  objectFit: "contain",
  border: "1px solid rgba(0, 0, 0, 0.12)",
  borderRadius: 1,
  bgcolor: "action.hover",
};

const HtmlBlock = ({ html, imageSize = "email" }) => (
  <Box
    sx={{
      ...formattedHtmlSx,
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 1,
      p: 1.5,
      minHeight: imageSize === "email" ? 96 : undefined,
      maxHeight: 240,
      overflow: "auto",
      maxWidth: imageSize === "email" ? { xs: "100%", md: "75%" } : "100%",
      "& .notification-email-image-remove": {
        display: "none",
      },
      "& img": imageSize === "email"
        ? {
            display: "block !important",
            width: "auto !important",
            height: "auto !important",
            maxWidth: "120px !important",
            maxHeight: "80px !important",
            objectFit: "contain",
          }
        : { maxWidth: "100%" },
    }}
    dangerouslySetInnerHTML={{ __html: html || "-" }}
  />
);

const HtmlValue = ({ html }) => {
  const markup = String(html || "").trim();
  if (!markup) return "-";
  return (
    <Box
      sx={{ ...formattedHtmlSx, whiteSpace: "normal" }}
      dangerouslySetInnerHTML={{ __html: markup }}
    />
  );
};

const ViewField = (props) => <InfoField valueWeight={400} {...props} />;

const TemplateView = ({ template, onClose }) => {
  const type = template?.notificationType;
  const content = normalizeTemplateContent(type, template?.content);
  const imageUrl = (content.image || "").trim();
  const ctas = Array.isArray(content.ctas) ? content.ctas : [];
  const categoryOptions = getPushCategoryOptionsForType(content.type);

  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      <Paper sx={emailSectionCardSx}>
        <ViewField label="Template Name" value={template?.templateName || "-"} />
        <ViewField
          label="Notification Type"
          value={getNotificationTemplateTypeLabel(type)}
        />
        <ViewField label="Last Updated" value={formatDate(template?.lastUpdated)} />
      </Paper>

      <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 2 }}>
        Content
      </Typography>
      <Paper sx={emailSectionCardSx}>
        {type === notificationTemplateTypes.push.value && (
          <>
            <ViewField
              label="Severity"
              value={getOptionLabel(notificationSeverityOptions, content.severity, "-")}
            />
            <ViewField
              label="Type"
              value={getOptionLabel(pushNotificationTypeOptions, content.type, content.type || "-")}
            />
            <ViewField
              label="Category"
              value={getOptionLabel(categoryOptions, content.category, content.category || "-")}
            />
            <ViewField label="Title" value={content.title || "-"} />
            <ViewField label="Description" wrap>
              <HtmlValue html={content.description} />
            </ViewField>
            <ViewField label="Target Screen" value={content.targetScreen || "-"} />
            {imageUrl ? (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                  Image URL
                </Typography>
                <Box
                  component="img"
                  src={imageUrl}
                  alt="Template"
                  sx={previewImageSx}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.75, overflowWrap: "anywhere" }}
                >
                  {imageUrl}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ mt: 2 }}>
                <ViewField label="Image URL" value="-" />
              </Box>
            )}
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                CTA
              </Typography>
              {ctas.length ? (
                ctas.map((cta, index) => (
                  <Typography key={`${cta.label}-${index}`} variant="body2" sx={{ mb: 0.5 }}>
                    {cta.label || "-"} → {cta.target || "-"}
                  </Typography>
                ))
              ) : (
                <Typography variant="body2">-</Typography>
              )}
            </Box>
          </>
        )}

        {type === notificationTemplateTypes.email.value && (
          <>
            <ViewField label="Subject" value={content.subject || "-"} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Body
            </Typography>
            <HtmlBlock html={content.html} />
          </>
        )}

        {type === notificationTemplateTypes.sms.value && (
          <ViewField label="Message" value={content.body || "-"} />
        )}
      </Paper>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2.5 }}>
        <AppButton variant="ghost" size="medium" onClick={onClose}>
          Close
        </AppButton>
      </Box>
    </Box>
  );
};

export default TemplateView;
